import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
// types
import { HTTaiKhoan } from 'src/types/api-response';
// hooks (Sử dụng hook hoặc gọi trực tiếp API tùy project của bạn)
import { PASSWORD_MSG, PASSWORD_REGEX } from 'src/constants';
import { useAccount } from './hooks';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  account: HTTaiKhoan;
};

export default function AccountChangePasswordDialog({ open, onClose, account }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  
  // Giả sử bạn đã có hàm updatePassword trong hook useAccount
  // Nếu chưa, bạn có thể import accountApiService và gọi trực tiếp
  const { updatePassword } = useAccount(); 

 const ChangePassSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('Mật khẩu mới là bắt buộc')
    .matches(PASSWORD_REGEX, PASSWORD_MSG), // Dùng chung Regex và Message
    
  confirmNewPassword: Yup.string()
    .required('Xác nhận mật khẩu là bắt buộc')
    .oneOf([Yup.ref('newPassword')], 'Mật khẩu xác nhận không khớp'),
});

  const methods = useForm({
    resolver: yupResolver(ChangePassSchema),
    defaultValues: {
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      // Gọi API cập nhật mật khẩu
      // ID được lấy từ props account, không hiển thị trên form
      await updatePassword(account.id!, data.newPassword);

      enqueueSnackbar('Đổi mật khẩu thành công!', { variant: 'success' });
      reset();
      onClose();
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(error.message || 'Không thể đổi mật khẩu', { variant: 'error' });
    }
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Đổi mật khẩu: {account.tenTaiKhoan}
      </DialogTitle>

      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {/* Field ID ẩn, thực tế không cần render input hidden vì ta dùng biến account.id trực tiếp */}
            
            <RHFTextField
              name="newPassword"
              label="Mật khẩu mới"
              type="password"
              InputProps={{
                endAdornment: <Iconify icon="solar:lock-password-bold" sx={{ color: 'text.disabled' }} />,
              }}
            />

            <RHFTextField
              name="confirmNewPassword"
              label="Xác nhận mật khẩu mới"
              type="password"
              InputProps={{
                endAdornment: <Iconify icon="solar:lock-password-bold" sx={{ color: 'text.disabled' }} />,
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            Hủy
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Lưu thay đổi
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}